import { useEffect, useState } from "react";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { hasAdminPermission } from "../../contracts/admin-permissions";
import {
  ADMIN_DEFAULT_TIME_RANGE_DAYS,
  ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  type AdminJobQuery,
  type AdminProjectionFreshness,
} from "../../contracts/admin-read-models";
import {
  createIdleAdminScreenState,
  createLoadingAdminScreenState,
  type AdminScreenState,
} from "../../contracts/admin-screen-state";
import { createAdminPermissionDeniedState } from "../../shared/query/create-admin-permission-denied-state";
import { toAdminScreenErrorState } from "../../shared/query/to-admin-screen-error-state";
import { searchJobs } from "./jobs-use-cases";
import { toJobMonitorViewModel, type JobMonitorViewModel } from "./job-monitor-view-model";

export type JobMonitorScreenModelDependencies = AdminDashboardDependencies & {
  locale?: AdminDashboardLocale;
  query?: AdminJobQuery;
  preserveExplicitUpperBound?: boolean;
};

export type JobMonitorScreenModel = AdminScreenState<JobMonitorViewModel>;

function createDefaultJobQuery(): AdminJobQuery {
  const now = new Date();

  return {
    timeRange: {
      from: new Date(now.getTime() - ADMIN_DEFAULT_TIME_RANGE_DAYS * 24 * 60 * 60 * 1000).toISOString(),
      to: now.toISOString(),
    },
    limit: ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  };
}

function deriveFreshnessPriority(
  sourceFreshness: Record<string, AdminProjectionFreshness>,
): "stale" | "partial" | "ready" {
  const values = Object.values(sourceFreshness);

  if (values.includes("stale")) {
    return "stale";
  }

  if (values.includes("partial") || values.includes("unavailable")) {
    return "partial";
  }

  return "ready";
}

function createDependenciesKey(dependencies: JobMonitorScreenModelDependencies): string {
  return JSON.stringify({
    roles: dependencies.adminRoles,
    query: dependencies.query ?? null,
  });
}

export async function resolveJobMonitorScreenModel(
  dependencies: JobMonitorScreenModelDependencies,
): Promise<JobMonitorScreenModel> {
  if (!hasAdminPermission(dependencies.adminPermissions, "admin.jobs.read")) {
    return createAdminPermissionDeniedState(
      dependencies.locale ?? "ko",
      {
        en: "Job monitor read permission denied.",
        ko: "작업 모니터 조회 권한이 없습니다.",
      },
      "admin_jobs_permission_denied",
    );
  }

  try {
    const resolvedQuery = dependencies.query ?? createDefaultJobQuery();
    const envelope = await searchJobs(
      dependencies.queryPort,
      resolvedQuery,
    );

    if (envelope.data.items.length === 0) {
      return {
        status: "empty",
      };
    }

    const viewModel = toJobMonitorViewModel(
      envelope.data,
      envelope.meta,
      resolvedQuery,
      dependencies.preserveExplicitUpperBound ?? false,
      dependencies.locale ?? "ko",
    );
    const freshnessPriority = deriveFreshnessPriority(envelope.meta.sourceFreshness);

    if (freshnessPriority === "stale") {
      return {
        status: "stale",
        viewModel,
      };
    }

    if (freshnessPriority === "partial") {
      return {
        status: "partial",
        viewModel,
      };
    }

    return {
      status: "ready",
      viewModel,
    };
  } catch (error) {
    return toAdminScreenErrorState(dependencies.locale ?? "ko", error);
  }
}

export function useJobMonitorScreenModel(
  dependencies: JobMonitorScreenModelDependencies,
  refreshSignal?: number,
): JobMonitorScreenModel {
  const [state, setState] = useState<JobMonitorScreenModel>(createIdleAdminScreenState());
  const dependenciesKey = createDependenciesKey(dependencies);

  useEffect(() => {
    let cancelled = false;

    setState((current) => createLoadingAdminScreenState(current.viewModel));

    void resolveJobMonitorScreenModel(dependencies).then((nextState) => {
      if (!cancelled) {
        setState(nextState);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dependencies.queryPort, dependenciesKey, refreshSignal]);

  return state;
}
